import { navigateTo, useRoute, useRuntimeConfig, useState } from 'nuxt/app'

type OnboardingProfile = {
  short_name?: string | null
  type?: string | null
  tin?: string | null
}

type OnboardingCacheEntry = {
  isComplete: boolean
  checkedAt: number
}

const ONBOARDING_WHITELIST = new Set([
  '/login',
  '/register',
  '/offer',
  '/invite',
  '/logout',
])

const REQUISITES_PREFIX = '/account/requisites'

const inflightChecks = new Map<number, Promise<boolean>>()

export function isClientProfileComplete(profile: OnboardingProfile | null | undefined): boolean {
  return !!(profile?.short_name && profile?.type && profile?.tin)
}

function shouldSkipOnboardingRedirect(path: string): boolean {
  return ONBOARDING_WHITELIST.has(path) || path.startsWith(REQUISITES_PREFIX)
}

function cacheKey(clientId: number): string {
  return String(clientId)
}

async function fetchClientProfile(clientId: number): Promise<OnboardingProfile | null> {
  const config = useRuntimeConfig()
  const token = process.client ? (localStorage.getItem('access_token') || '') : ''
  const timeZone = process.client
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    : 'UTC'
  
  try {
    const res = await $fetch<{ data?: OnboardingProfile } | OnboardingProfile>(
      `/api/clients/${clientId}`,
      {
        baseURL: config.public.apiBaseUrl,
        headers: {
          Accept: 'application/json',
          'X-Timezone': timeZone,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'X-Client-Id': String(clientId),
        },
      },
    )

    if (res && typeof res === 'object' && 'data' in res && res.data)
      return res.data

    return res as OnboardingProfile
  }
  catch {
    return null
  }
}

export function useClientOnboarding() {
  const onboardingByClientId = useState<Record<string, OnboardingCacheEntry>>(
    'clientOnboardingById',
    () => ({}),
  )

  function getCachedStatus(clientId: number): boolean | null {
    const entry = onboardingByClientId.value[cacheKey(clientId)]
    return entry?.isComplete ?? null
  }

  function setCachedStatus(clientId: number, isComplete: boolean) {
    onboardingByClientId.value = {
      ...onboardingByClientId.value,
      [cacheKey(clientId)]: { isComplete, checkedAt: Date.now() },
    }
  }

  function markOnboardingComplete(
    clientId: number,
    profile: OnboardingProfile,
  ) {
    setCachedStatus(clientId, isClientProfileComplete(profile))
  }

  function invalidateOnboarding(clientId: number) {
    const key = cacheKey(clientId)
    if (!(key in onboardingByClientId.value))
      return

    const next = { ...onboardingByClientId.value }
    delete next[key]
    onboardingByClientId.value = next
  }

  async function resolveOnboardingStatus(
    clientId: number,
    { force = false }: { force?: boolean } = {},
  ): Promise<boolean> {
    if (!force) {
      const cached = getCachedStatus(clientId)
      if (cached !== null)
        return cached
    }

    const existing = inflightChecks.get(clientId)
    if (existing)
      return existing

    const promise = (async () => {
      const profile = await fetchClientProfile(clientId)
      const isComplete = isClientProfileComplete(profile)
      setCachedStatus(clientId, isComplete)
      return isComplete
    })()

    inflightChecks.set(clientId, promise)

    try {
      return await promise
    }
    finally {
      inflightChecks.delete(clientId)
    }
  }

  async function checkOnboarding(
    clientId: number,
    options: { force?: boolean } = {},
  ): Promise<boolean> {
    if (!clientId || Number.isNaN(clientId))
      return true

    const isComplete = await resolveOnboardingStatus(clientId, options)

    if (isComplete)
      return true

    const route = useRoute()
    if (shouldSkipOnboardingRedirect(route.path))
      return false

    await navigateTo({
      path: '/account/requisites',
      query: { notice: 'onboarding', highlight: 'short_name' },
      replace: true,
    })

    return false
  }

  function clearAllOnboardingCache() {
    onboardingByClientId.value = {}
    inflightChecks.clear()
  }

  return {
    checkOnboarding,
    markOnboardingComplete,
    invalidateOnboarding,
    clearAllOnboardingCache,
    isClientProfileComplete,
    getCachedStatus,
  }
}
